import React, { useState, useRef } from "react";
import Avatar from '@mui/material/Avatar';
import { Stack, IconButton, Box } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MeatballMenu from "../MeatballMenu/MeatballMenu";
import Colours from "../ColourFunction/ColourFunction";
import NewDialog from "../NewDialog/NewDialog";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel
} from '@mui/material';

const UserListing = (props) => {
  const {
    list = [],
    authToken,
    noIcon,
    showDeletedUsers = false, // used to initialise collapse open state
    btnFunc,
    btnLicenceFunc,
    resendInviteFunc,
    currentUserSub,
    currentLicenceInUse,
    onUserDeleted,
    ...others
  } = props;

  const [orderBy, setOrderBy] = useState('FullName');
  const [order, setOrder] = useState('asc');
  const [showDeleted, setShowDeleted] = useState(!!showDeletedUsers);
  const [showPending, setShowPending] = useState(false);
  const [showActive, setShowActive] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const dialogRef = useRef();

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sort the full list, then split into active / deleted
  const sortedList = [...list].sort((a, b) => {
    // guard if property missing
    const aa = a?.[orderBy] ?? '';
    const bb = b?.[orderBy] ?? '';
    if (aa < bb) return order === 'asc' ? -1 : 1;
    if (aa > bb) return order === 'asc' ? 1 : -1;
    return 0;
  });

  // Function to get menu items based on user status
  const getMenuItemsForUser = (item) => {
    // Deleted users → no menu
    if (item.IsDeleted) {
      return [];
    }

    // Pending users
    if (item.InvitedStatus === 'PENDING') {
      return [
        {
          key: 'resend-invite',
          label: 'Resend invitation',
          onClick: () => resendInviteFunc?.(item.Email, item.FullName)
        },
        {
          key: 'delete-user',
          label: 'Delete user',
          onClick: () => handleDeleteUser(item)
        }
      ];
    }

    // Active users (default)
    return [
      {
        key: 'delete-user',
        label: 'Delete user',
        onClick: () => handleDeleteUser(item)
      }
    ];
  };

  const handleDeleteUser = (user) => {
    const dialogConfig = {
      title: "Are you sure?",
      components: "Are you sure you would like to delete this user?",
      cancelButton: 1,
      buttons: {
        bottom: [
          {
            label: '❌ DELETE',
            disable: deleteInProgress,
            onClick: () => {
              setDeleteInProgress(true);
              btnFunc(user.user_sub);
              dialogRef.current.modalCloseAction();
              onUserDeleted?.();
              setDeleteInProgress(false);
            }
          }
        ]
      }
    };

    dialogRef.current.handleOpen(dialogConfig);
  };



  const ACTIVE_PREVIEW_COUNT = 5;
  const [showAllActive, setShowAllActive] = useState(false);

  const activeUsers = sortedList.filter(u => !u.IsDeleted && u.InvitedStatus !== 'PENDING');
  const deletedUsers = sortedList.filter(u => u.IsDeleted);
  const pendingUsers = sortedList.filter(u => u.InvitedStatus === 'PENDING' && !u.IsDeleted);

  const visibleActiveUsers = showAllActive
    ? activeUsers
    : activeUsers.slice(0, ACTIVE_PREVIEW_COUNT);

  const hasMoreActive = activeUsers.length > ACTIVE_PREVIEW_COUNT;

  return (
    <>
      <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>
              <TableSortLabel
                active={orderBy === 'FullName'}
                direction={orderBy === 'FullName' ? order : 'asc'}
                onClick={() => handleSort('FullName')}
              >
                Full Name
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>Email</TableCell>
            <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>
              {/* Qlik Permissions header (commented out) */}
            </TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {/* Active users */}
          {/* {activeUsers.map((item, index) => ( */}
          {visibleActiveUsers.map((item, index) => (
            <TableRow
              key={item.user_sub ?? `active-${index}`}
              sx={{ bgcolor: item.IsDeleted ? 'grey.200' : 'background.paper', '& td': { paddingY: 0.5 } }}
            >
              <TableCell>
                <Avatar {...Colours.ColourFunction(item.FullName)} />
              </TableCell>
              <TableCell>{item.FullName}</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>{item.Email}</TableCell>
              <TableCell>
                {/* placeholder for licence column if needed */}
              </TableCell>
              <TableCell>
                {currentUserSub !== item.user_sub && (
                  <Stack direction="row" spacing={2}>
                    <MeatballMenu
                      menuItems={getMenuItemsForUser(item)}
                    />
                  </Stack>
                )}
              </TableCell>
            </TableRow>
          ))}
          {hasMoreActive && (
            <TableRow>
              <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    px: 2,
                    py: 1,
                    cursor: 'pointer'
                  }}
                  onClick={() => setShowAllActive(prev => !prev)}
                >
                  <IconButton
                    size="small"
                    aria-label={showAllActive ? 'Show fewer active users' : 'Show all active users'}
                  >
                    {showAllActive ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </IconButton>

                  <Box
                    component="span"
                    sx={{ ml: 1, fontWeight: 'bold', userSelect: 'none' }}
                  >
                    {showAllActive
                      ? 'Show fewer active users'
                      : `Show all active users (${activeUsers.length})`}
                  </Box>
                </Box>
              </TableCell>
            </TableRow>
          )}


          {/* Collapsible toggle row only shown if there are deleted users */}
          {deletedUsers.length > 0 && (
            <TableRow>
              <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    px: 2,
                    py: 1,
                    cursor: 'pointer'
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => setShowDeleted(prev => !prev)}
                    aria-label={showDeleted ? 'Hide deleted users' : 'Show deleted users'}
                  >
                    {showDeleted ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </IconButton>

                  <Box
                    component="span"
                    onClick={() => setShowDeleted(prev => !prev)}
                    sx={{ ml: 1, fontWeight: 'bold', userSelect: 'none' }}
                  >
                    {showDeleted ? 'Hide Deleted Users' : `Show Deleted Users (${deletedUsers.length})`}
                  </Box>
                </Box>
              </TableCell>
            </TableRow>
          )}

          {/* Collapsed deleted users placed in a single cell spanning the table */}
          {deletedUsers.length > 0 && (
            <TableRow>
              <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                <Collapse in={showDeleted} timeout="auto" unmountOnExit>
                  <Box sx={{ py: 1 }}>
                    <Table size="small">
                      <TableBody>
                        {deletedUsers.map((item, index) => (
                          <TableRow
                            key={item.user_sub ?? `deleted-${index}`}
                            sx={{ bgcolor: 'grey.200', '& td': { paddingY: 0.5 } }}
                          >
                            <TableCell>
                              <Avatar {...Colours.ColourFunction(item.FullName)} />
                            </TableCell>
                            <TableCell>{item.FullName}</TableCell>
                            <TableCell sx={{ color: 'text.secondary' }}>{item.Email}</TableCell>
                            <TableCell>
                              {/* licence column placeholder */}
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={2}>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </Collapse>
              </TableCell>
            </TableRow>
          )}
          {/* Collapsible toggle row only shown if there are deleted users */}
          {pendingUsers.length > 0 && (
            <TableRow>
              <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    px: 2,
                    py: 1,
                    cursor: 'pointer'
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => setShowPending(prev => !prev)}
                    aria-label={showPending ? 'Hide pending users' : 'Show pending users'}
                  >
                    {showPending ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </IconButton>

                  <Box
                    component="span"
                    onClick={() => setShowPending(prev => !prev)}
                    sx={{ ml: 1, fontWeight: 'bold', userSelect: 'none' }}
                  >
                    {showPending ? 'Hide Pending Users' : `Show Pending Users (${pendingUsers.length})`}
                  </Box>
                </Box>
              </TableCell>
            </TableRow>
          )}

          {/* Collapsed deleted users placed in a single cell spanning the table */}
          {pendingUsers.length > 0 && (
            <TableRow>
              <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                <Collapse in={showPending} timeout="auto" unmountOnExit>
                  <Box sx={{ py: 1 }}>
                    <Table size="small">
                      <TableBody>
                        {pendingUsers.map((item, index) => (
                          <TableRow
                            key={item.user_sub ?? `pending-${index}`}
                            sx={{ bgcolor: 'grey.200', '& td': { paddingY: 0.5 } }}
                          >
                            <TableCell>
                              <Avatar {...Colours.ColourFunction(item.FullName)} />
                            </TableCell>
                            <TableCell>{item.FullName}</TableCell>
                            <TableCell sx={{ color: 'text.secondary' }}>{item.Email}</TableCell>
                            <TableCell sx={{ color: 'text.secondary' }}>{item.InvitedStatus}</TableCell>
                            <TableCell>
                              {/* licence column placeholder */}
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={2}>
                                <MeatballMenu
                                  menuItems={getMenuItemsForUser(item)}
                                />
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </Collapse>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
    <NewDialog ref={dialogRef} />
    </>
  );
};

export default UserListing;